import React, { useEffect, useRef, useState } from 'react'
import { requestBuilderCustomerSignIn } from './api'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const CUSTOMER_SIGN_IN_RESEND_COOLDOWN_MS = 30_000
export const CUSTOMER_SIGN_IN_LINK_TTL_MS = 15 * 60 * 1000

export default function CustomerSignInGate({
  vendorToken,
  builderDraftPublicId,
  initialEmail = '',
  pendingRequest = null,
  cancelLabel = 'Back to artwork setup',
  onCancel,
  onUseDifferentEmail,
  onRequestAccepted,
}) {
  const [email, setEmail] = useState(pendingRequest?.email || initialEmail)
  const [state, setState] = useState(pendingRequest ? 'pending' : 'form')
  const [localPendingRequest, setLocalPendingRequest] = useState(pendingRequest)
  const [error, setError] = useState(null)
  const [now, setNow] = useState(() => Date.now())
  const dialogRef = useRef(null)
  const emailInputRef = useRef(null)
  const pendingHeadingRef = useRef(null)

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        return
      }
      if (event.key !== 'Tab') return

      const focusable = dialogRef.current?.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
      if (!focusable?.length) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    if (pendingRequest) {
      setEmail(pendingRequest.email)
      setLocalPendingRequest(pendingRequest)
      setState('pending')
      setError(null)
    }
  }, [pendingRequest])

  useEffect(() => {
    if (state === 'pending' || state === 'resending') pendingHeadingRef.current?.focus()
    else emailInputRef.current?.focus()
  }, [state])

  useEffect(() => {
    if (state !== 'pending' && state !== 'resending') return undefined

    const intervalId = window.setInterval(() => setNow(Date.now()), 1_000)
    return () => window.clearInterval(intervalId)
  }, [state])

  const sendLink = async ({ resend = false } = {}) => {
    const normalizedEmail = email.trim()
    if (!EMAIL_RE.test(normalizedEmail) || !builderDraftPublicId) {
      setError('Enter a valid email address.')
      return
    }

    setState(resend ? 'resending' : 'submitting')
    setError(null)
    try {
      const response = await requestBuilderCustomerSignIn(vendorToken, normalizedEmail, builderDraftPublicId)
      const requestedAt = Date.now()
      const request = {
        email: normalizedEmail,
        handoffToken: response.handoff_token || null,
        requestedAt,
        expiresAt: requestedAt + CUSTOMER_SIGN_IN_LINK_TTL_MS,
      }
      setNow(requestedAt)
      setLocalPendingRequest(request)
      setState('pending')
      onRequestAccepted?.({
        ...request,
      })
    } catch {
      setState(resend ? 'pending' : 'form')
      setError('We could not send the sign-in link. Please try again.')
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    await sendLink()
  }

  const handleUseDifferentEmail = () => {
    onUseDifferentEmail?.()
    setLocalPendingRequest(null)
    setState('form')
    setError(null)
  }

  const activeRequest = pendingRequest || localPendingRequest
  const resendAvailableAt = Number(activeRequest?.requestedAt || 0) +
    CUSTOMER_SIGN_IN_RESEND_COOLDOWN_MS
  const resendSeconds = Math.max(0, Math.ceil((resendAvailableAt - now) / 1_000))
  const linkExpired = Boolean(activeRequest?.expiresAt && now >= activeRequest.expiresAt)
  const pending = state === 'pending' || state === 'resending'

  return (
    <div className="customer-sign-in-gate" data-testid="customer-sign-in-gate">
      <div className="customer-sign-in-gate__backdrop" aria-hidden="true" />
      <div
        ref={dialogRef}
        className="customer-sign-in-gate__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-sign-in-gate-title"
        aria-describedby="customer-sign-in-gate-description"
      >
        <h2 id="customer-sign-in-gate-title">
          {pending ? 'Check your email to continue' : 'Sign in to save this gang sheet and continue'}
        </h2>
        <p id="customer-sign-in-gate-description">
          {pending
            ? `We sent a secure sign-in link to ${activeRequest?.email || email}.`
            : 'Your account keeps every design you build with this shop in one place. Return later, reorder previous gang sheets, and access saved designs.'}
        </p>

        {pending ? (
          <div className="customer-sign-in-gate__success-state">
            <div
              ref={pendingHeadingRef}
              className="customer-sign-in-gate__success"
              role="status"
              tabIndex="-1"
            >
              {linkExpired
                ? 'This sign-in link has expired. Send a new link to continue.'
                : 'Open the link in your email. Keep this builder tab open—we’ll continue automatically after you sign in.'}
            </div>
            <p className="customer-sign-in-gate__continue-copy">
              Waiting for sign-in confirmation…
            </p>
            {error && <div className="customer-sign-in-gate__error" role="alert">{error}</div>}
            <div className="customer-sign-in-gate__actions">
              <button
                type="button"
                className="customer-sign-in-gate__primary-action"
                disabled={state === 'resending' || (!linkExpired && resendSeconds > 0)}
                onClick={() => sendLink({ resend: true })}
              >
                {state === 'resending'
                  ? 'Sending…'
                  : linkExpired
                    ? 'Send a new link'
                    : resendSeconds > 0
                      ? `Resend in ${resendSeconds}s`
                      : 'Resend sign-in link'}
              </button>
              <button type="button" onClick={handleUseDifferentEmail}>
                Use a different email
              </button>
              <button type="button" onClick={onCancel}>
                {cancelLabel}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label htmlFor="customer-sign-in-email">Email address</label>
            <input
              id="customer-sign-in-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              ref={emailInputRef}
              onChange={(event) => setEmail(event.target.value)}
              disabled={state === 'submitting'}
            />
            {error && <div className="customer-sign-in-gate__error" role="alert">{error}</div>}
            <button type="submit" disabled={state === 'submitting'}>
              {state === 'submitting' ? 'Sending…' : 'Send sign-in link'}
            </button>
            <button
              type="button"
              className="customer-sign-in-gate__form-cancel"
              onClick={onCancel}
            >
              {cancelLabel}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
